import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../utils/api';

const SimpleCard = ({ label, desc, to, color, icon, badge }) => (
  <Link to={to} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
    <div style={{
      position: 'relative', background: '#fff', border: '1px solid #dee2e6',
      borderRadius: '10px', padding: '14px', display: 'flex',
      alignItems: 'flex-start', gap: '12px', borderLeft: `4px solid ${color}`, height: '100%'
    }}>
      {badge > 0 && (
        <span style={{
          position: 'absolute', top: '-8px', right: '-8px', background: '#dc3545',
          color: '#fff', borderRadius: '50%', width: '22px', height: '22px',
          fontSize: '11px', fontWeight: '700', display: 'flex',
          alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.3)'
        }}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <div style={{ fontSize: '24px', marginTop: '2px' }} aria-hidden="true">{icon}</div>
      <div>
        <div style={{ color: '#212529', fontSize: '13px', fontWeight: '600', marginBottom: '3px' }}>{label}</div>
        <div style={{ color: '#6c757d', fontSize: '11px' }}>{desc}</div>
      </div>
    </div>
  </Link>
);

const GroupCard = ({ label, icon, color, tagColor, tags, badge }) => (
  <div style={{
    position: 'relative', background: '#fff', border: '1px solid #dee2e6',
    borderRadius: '10px', padding: '14px', display: 'flex',
    alignItems: 'flex-start', gap: '12px', borderLeft: `4px solid ${color}`, height: '100%'
  }}>
    {badge > 0 && (
      <span style={{
        position: 'absolute', top: '-8px', right: '-8px', background: '#dc3545',
        color: '#fff', borderRadius: '50%', width: '22px', height: '22px',
        fontSize: '11px', fontWeight: '700', display: 'flex',
        alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.3)'
      }}>
        {badge > 99 ? '99+' : badge}
      </span>
    )}
    <div style={{ fontSize: '24px', marginTop: '2px' }} aria-hidden="true">{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={{ color: '#212529', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>{label}</div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {tags.filter(t => t.show).map(tag => (
          <Link
            key={tag.to}
            to={tag.to}
            style={{
              textDecoration: 'none', background: tagColor.bg, color: tagColor.text,
              borderRadius: '4px', padding: '3px 10px', fontSize: '11px', fontWeight: '500'
            }}
          >
            {tag.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const LandingPage = () => {
  const { user, hasScope, isSuperuser } = useAuth();
  const [notifCounts, setNotifCounts] = useState({ orders: 0, qsc: 0 });

  useEffect(() => {
    notificationsAPI.getAll({ limit: 100 })
      .then(res => {
        const unread = (res.data.data || []).filter(n => !n.read);
        setNotifCounts({
          orders: unread.filter(n => n.type.startsWith('order_')).length,
          qsc: unread.filter(n => n.type.startsWith('qsc_')).length,
        });
      })
      .catch(() => {});
  }, []);

  const simpleCards = [
    { label: 'รายการคำสั่งซื้อ', desc: 'คำสั่งซื้อที่จัดส่งเสร็จสิ้นแล้ว', to: '/dashboard', color: '#0d6efd', icon: '🏠', show: true },
    { label: 'รออนุมัติ', desc: 'คำสั่งซื้อที่รอการอนุมัติ', to: '/pending-orders', color: '#ffc107', icon: '⏳', show: hasScope('orders:manage'), badge: notifCounts.orders },
    { label: 'สร้างคำสั่งซื้อ', desc: 'สั่งวัตถุดิบจากสาขา', to: '/orders/create', color: '#198754', icon: '➕', show: hasScope('orders:create') && !hasScope('orders:manage') },
    { label: 'คำสั่งซื้อของฉัน', desc: 'ติดตามคำสั่งซื้อที่สร้างไว้', to: '/my-orders', color: '#0d6efd', icon: '📋', show: hasScope('orders:create') && !hasScope('orders:manage') },
    { label: 'รายงาน HQ', desc: 'รายงานสรุปสำหรับสำนักงานใหญ่', to: '/hq-report', color: '#dc3545', icon: '📊', show: hasScope('reports:hq') },
    { label: 'จัดการการจัดส่ง', desc: 'บันทึกและติดตามการจัดส่ง', to: '/shipping', color: '#6c757d', icon: '🚛', show: hasScope('shipping') },
    { label: 'ซัพพลายเออร์', desc: 'จัดการรายชื่อซัพพลายเออร์', to: '/suppliers', color: '#0d6efd', icon: '🏭', show: hasScope('suppliers') },
    { label: 'ผู้ซื้อ', desc: 'จัดการรายชื่อผู้ซื้อ', to: '/buyers', color: '#0d6efd', icon: '🛒', show: hasScope('buyers') },
    { label: 'จัดการ VAT', desc: 'ตั้งค่าอัตราภาษีมูลค่าเพิ่ม', to: '/vat', color: '#ffc107', icon: '💹', show: hasScope('vat') },
    { label: 'จัดการผู้ใช้', desc: 'เพิ่ม / แก้ไขบัญชีผู้ใช้', to: '/users', color: '#dc3545', icon: '👥', show: isSuperuser() },
  ];

  const groupCards = [
    {
      label: 'คลังสินค้า', icon: '📦', color: '#0dcaf0',
      tagColor: { bg: '#cff4fc', text: '#055160' },
      badge: 0,
      show: hasScope('fulfillment') || hasScope('stock:intake') || hasScope('stock:outtake') || hasScope('stock:monitoring'),
      tags: [
        { label: 'เบิกตามคำสั่งซื้อ', to: '/fulfillment', show: hasScope('fulfillment') },
        { label: 'รับสต็อกเข้า', to: '/stock/intake', show: hasScope('stock:intake') },
        { label: 'เบิกสต็อกออก', to: '/stock/outtake', show: hasScope('stock:outtake') },
        { label: 'ตรวจสอบสต็อก', to: '/stock/monitoring', show: hasScope('stock:monitoring') },
      ],
    },
    {
      label: 'จัดการวัตถุดิบ', icon: '🧱', color: '#6f42c1',
      tagColor: { bg: '#e9d8fd', text: '#4a1d96' },
      badge: 0,
      show: hasScope('materials:manage'),
      tags: [
        { label: 'รายการวัตถุดิบ', to: '/materials', show: true },
        { label: 'เพิ่มวัตถุดิบ', to: '/materials/create', show: true },
      ],
    },
    {
      label: 'QSC', icon: '✅', color: '#20c997',
      tagColor: { bg: '#d1f5ea', text: '#065f46' },
      badge: notifCounts.qsc,
      show: isSuperuser() || hasScope('qsc:manage') || hasScope('qsc:improvements') || hasScope('qsc:view'),
      tags: [
        { label: 'รายการประเมิน', to: '/qsc', show: hasScope('qsc:manage') || hasScope('qsc:improvements') || hasScope('qsc:view') },
        { label: 'สร้างแบบประเมิน', to: '/qsc/new', show: hasScope('qsc:manage') },
        { label: 'รีวิวการปรับปรุง', to: '/qsc/improvements', show: hasScope('qsc:improvements') },
        { label: 'จัดการ Templates', to: '/qsc/templates', show: hasScope('qsc:manage') },
      ],
    },
    {
      label: 'ต้นทุนสูตร', icon: '🧾', color: '#fd7e14',
      tagColor: { bg: '#ffe5c2', text: '#7c2d12' },
      badge: 0,
      show: hasScope('recipes'),
      tags: [
        { label: 'ประวัติสูตร', to: '/recipes', show: true },
        { label: 'คำนวณสูตรใหม่', to: '/recipes/new', show: true },
      ],
    },
  ];

  return (
    <Container fluid className="main-content">
      <div className="mb-4">
        <h2>ยินดีต้อนรับ, <strong>{user?.fullName || user?.username}</strong></h2>
        <p className="text-muted">เลือกฟีเจอร์ที่ต้องการใช้งาน</p>
      </div>
      <Row className="g-3">
        {simpleCards.filter(c => c.show).map(({ show, ...card }) => (
          <Col md={6} key={card.label}>
            <SimpleCard {...card} />
          </Col>
        ))}
        {groupCards.filter(c => c.show).map(({ show, ...card }) => (
          <Col md={6} key={card.label}>
            <GroupCard {...card} />
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default LandingPage;
